(() => {
  const sections = document.querySelectorAll('[data-ew-reels-section]');
  if (!sections.length) return;

  sections.forEach((section) => {
    const modal = section.querySelector('[data-ew-reel-modal]');
    const openers = Array.from(section.querySelectorAll('[data-ew-reel-open]'));
    const slides = Array.from(section.querySelectorAll('[data-ew-reel-slide]'));
    if (!modal || !openers.length || !slides.length) return;

    // Native autoplay can remain paused after loading below the fold.
    // Only play previews that intersect both the viewport and horizontal rail.
    const previews = Array.from(section.querySelectorAll('.ew-reel-card video'));
    const visiblePreviews = new Set();
    const pendingPlay = new WeakSet();
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const previewAllowed = (video) => visiblePreviews.has(video) &&
      !document.hidden && modal.hidden && !motion.matches;
    const resumePreview = (video) => {
      if (!previewAllowed(video)) {
        video.pause();
        return;
      }
      if (!video.paused || pendingPlay.has(video)) return;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      pendingPlay.add(video);
      video.play().then(() => {
        if (!previewAllowed(video)) video.pause();
      }).catch(() => {
        // Keep the poster and existing tap-to-watch button if autoplay is blocked.
      }).finally(() => pendingPlay.delete(video));
    };
    const syncPreviews = () => previews.forEach(resumePreview);
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting, intersectionRatio }) => {
        if (isIntersecting && intersectionRatio >= 0.2) visiblePreviews.add(target);
        else visiblePreviews.delete(target);
        resumePreview(target);
      });
    }, { threshold: [0, 0.2] }) : null;
    previews.forEach((video) => {
      if (observer) {
        video.autoplay = false;
        video.pause();
      }
      video.addEventListener('canplay', () => resumePreview(video));
      if (observer) observer.observe(video);
    });
    document.addEventListener('visibilitychange', () => {
      syncPreviews();
      if (document.hidden) pauseAll();
    });
    // A real gesture lets browsers retry previews blocked by autoplay policy.
    section.addEventListener('pointerup', syncPreviews);
    motion.addEventListener('change', syncPreviews);
    window.addEventListener('pageshow', syncPreviews);

    let index = 0;
    let lastFocused = null;

    const activeSlide = () => slides[index];
    const activeVideo = () => activeSlide()?.querySelector('video');

    const pauseAll = () => {
      slides.forEach((slide) => {
        const video = slide.querySelector('video');
        if (video) video.pause();
      });
    };

    const show = (nextIndex) => {
      index = (nextIndex + slides.length) % slides.length;
      pauseAll();
      slides.forEach((slide, i) => {
        const active = i === index;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
      });
      const video = activeVideo();
      if (video) {
        video.currentTime = 0;
        video.muted = true;
        video.play().catch(() => {});
      }
      const products = activeSlide()?.querySelector('[data-ew-reel-products]');
      if (products) products.scrollLeft = 0;
    };

    const open = (nextIndex, trigger) => {
      lastFocused = trigger || document.activeElement;
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      syncPreviews();
      show(nextIndex);
      document.body.classList.add('ew-reel-modal-open');
      window.requestAnimationFrame(() => modal.querySelector('[data-ew-reel-close]')?.focus());
    };

    const close = () => {
      pauseAll();
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('ew-reel-modal-open');
      lastFocused?.focus?.();
      syncPreviews();
    };

    openers.forEach((opener) => {
      opener.addEventListener('click', () => open(Number(opener.dataset.ewReelOpen) || 0, opener));
    });

    modal.querySelectorAll('[data-ew-reel-close]').forEach((button) => button.addEventListener('click', close));
    modal.querySelector('[data-ew-reel-prev]')?.addEventListener('click', () => show(index - 1));
    modal.querySelector('[data-ew-reel-next]')?.addEventListener('click', () => show(index + 1));

    modal.addEventListener('click', async (event) => {
      const sound = event.target.closest('[data-ew-reel-sound]');
      if (sound) {
        const video = activeVideo();
        if (!video) return;
        video.muted = !video.muted;
        sound.classList.toggle('is-active', !video.muted);
        sound.querySelector('span').textContent = video.muted ? 'Sound' : 'Muted';
        return;
      }

      const like = event.target.closest('[data-ew-reel-like]');
      if (like) {
        like.classList.toggle('is-liked');
        const liked = like.classList.contains('is-liked');
        like.firstChild.textContent = liked ? '♥' : '♡';
        return;
      }

      const share = event.target.closest('[data-ew-reel-share]');
      if (share) {
        const productUrl = activeSlide()?.querySelector('.ew-reel-product__main')?.href || window.location.href;
        try {
          if (navigator.share) {
            await navigator.share({ title: document.title, url: productUrl });
          } else {
            await navigator.clipboard.writeText(productUrl);
            const label = share.querySelector('span');
            if (label) {
              const original = label.textContent;
              label.textContent = 'Copied';
              window.setTimeout(() => { label.textContent = original; }, 1400);
            }
          }
        } catch (_) {}
      }
    });

    document.addEventListener('keydown', (event) => {
      if (modal.hidden) return;
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowLeft') show(index - 1);
      if (event.key === 'ArrowRight') show(index + 1);
    });

    let touchStartX = null;
    modal.addEventListener('touchstart', (event) => {
      touchStartX = event.changedTouches[0]?.clientX ?? null;
    }, { passive: true });
    modal.addEventListener('touchend', (event) => {
      if (touchStartX === null) return;
      const endX = event.changedTouches[0]?.clientX ?? touchStartX;
      const delta = endX - touchStartX;
      touchStartX = null;
      if (Math.abs(delta) < 55) return;
      show(index + (delta < 0 ? 1 : -1));
    }, { passive: true });
  });
})();

